import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const LEDGER_KEY = "fl.activeLedgerId";

// Active ledger id is a short-lived UX pointer, not sensitive data
// (backend enforces access control via cookie + household membership
// on every request). It lives in sessionStorage rather than
// localStorage so it doesn't survive the tab being closed — a
// hardening step recommended by our code-review pass to keep any
// ledger-scoped context strictly per-session. Users who close the
// tab land back on their default (first) household on next login,
// which the auth checkAuth() path already handles.
export function getActiveLedgerId() {
  try {
    return sessionStorage.getItem(LEDGER_KEY) || null;
  } catch {
    return null;
  }
}

export function setActiveLedgerId(id) {
  try {
    if (id) sessionStorage.setItem(LEDGER_KEY, id);
    else sessionStorage.removeItem(LEDGER_KEY);
    // Also clear any legacy value that may still be lurking in
    // localStorage from before this migration — otherwise a stale
    // entry there would surface on the next hard reload if
    // sessionStorage is cleared.
    localStorage.removeItem(LEDGER_KEY);
  } catch {
    // ignore
  }
}

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Attach X-Ledger-Id on every request when a non-private ledger is active.
api.interceptors.request.use((config) => {
  const lid = getActiveLedgerId();
  if (lid) {
    config.headers = config.headers || {};
    config.headers["X-Ledger-Id"] = lid;
  } else if (config.headers) {
    delete config.headers["X-Ledger-Id"];
  }
  return config;
});

export const fileUrl = (path) => `${API}/files/${path}`;

// CATEGORIES is the legacy hardcoded fallback used by code paths that haven't
// yet been migrated to the dynamic AssetClassesContext. New components should
// use `useAssetClasses()` from "@/lib/assetClasses" instead — it returns the
// live per-ledger list including any user-defined classes.
export const CATEGORIES = [
  { value: "high_end_gear", label: "High-End Gear" },
  { value: "home_component", label: "Home Component" },
  { value: "fine_art", label: "Fine Art & Collectibles" },
  { value: "vehicle_marine", label: "Vehicles & Marine" },
  { value: "other", label: "Other" },
];

export const categoryLabel = (v) =>
  CATEGORIES.find((c) => c.value === v)?.label || (v ? v.replace(/_/g, " ") : "Other");

export const fmtMoney = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = typeof n === "string" ? parseFloat(n) : n;
  if (Number.isNaN(v)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v);
};

export const fmtDate = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return s;
  }
};

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
